// src/admin/FeaturedItemManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    getFeaturedItems,
    addFeaturedItem,
    updateFeaturedItem,
    deleteFeaturedItem
} from '../api'; // Adjust path as needed

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
// --- NEW: Imports for Color Selector ---
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem'; // Added MenuItem
import { alpha } from '@mui/system';

// Helper function to get Steam community image URL (consistent with other pages)
const getSteamImageUrl = (iconUrlPath, sizeSuffix = '/96fx96f') => {
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            return `${iconUrlPath}${sizeSuffix}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${sizeSuffix}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${sizeSuffix}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${sizeSuffix}`;
};

// --- NEW: Color options for the selector (same as ItemAddModal) ---
const colorOptions = [
    { value: 'F0F0F0', label: 'Default (Whiteish)' },
    { value: '809FEB', label: 'Light Blue' },
    { value: '3361FD', label: 'Blue' },
    { value: 'A947F5', label: 'Purple' },
    { value: 'FF1EE9', label: 'Pink' },
    { value: 'FF3F3F', label: 'Red' },
    { value: 'FFE819', label: 'Yellow' },
    { value: 'FF8430', label: 'Orange' },
];

// --- Add/Edit Modal Component ---
function FeaturedItemModal({ open, onClose, item, onSave }) {
    const [formData, setFormData] = useState({
        assetid: '',
        name: '',
        name_color: 'F0F0F0', // Default to one of the color options
        icon_url: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const isEditMode = Boolean(item); // Determine if editing or adding

    useEffect(() => {
        if (item) {
            // Editing existing item
            setFormData({
                assetid: item.assetid || '',
                name: item.name || '',
                name_color: item.name_color || 'F0F0F0',
                icon_url: item.icon_url || '',
            });
            setSaveError('');
        } else {
            // Adding new item - reset form
            setFormData({
                assetid: '',
                name: '',
                name_color: 'F0F0F0',
                icon_url: '',
            });
            setSaveError('');
        }
    }, [item, open]); // Reset form when item changes or modal opens

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError('');
        try {
            // Construct payload - ensure assetid is a number if needed by backend
            const payload = {
                ...formData,
                assetid: parseInt(formData.assetid, 10) || 0, // Ensure assetid is number
                // Ensure name_color doesn't include '#' if backend expects raw hex
                name_color: formData.name_color?.replace('#', ''),
            };

            // Validate required fields
            if (!payload.assetid || !payload.name || !payload.icon_url) {
                throw new Error("Asset ID, Name, and Icon URL are required.");
            }

            await onSave(payload, isEditMode); // Pass payload and mode to parent
            onClose(); // Close modal on success
        } catch (err) {
            const errMsg = err.response?.data?.title
                || err.response?.data?.message
                || err.message
                || (isEditMode ? 'Failed to update featured item.' : 'Failed to add featured item.');
            setSaveError(errMsg);
            console.error("Save featured item error:", err.response || err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Featured Item' : 'Add New Featured Item'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    <TextField
                        label="Asset ID"
                        name="assetid"
                        type="number" // Use number type for asset ID
                        value={formData.assetid}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="small"
                        disabled={isEditMode} // Disable Asset ID when editing
                        helperText={isEditMode ? "Asset ID cannot be changed." : "Unique identifier for the item."}
                    />
                    <TextField
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Display name of the item."
                    />
                    {/* --- MODIFIED: Name Color Selector --- */}
                    <FormControl fullWidth size="small" disabled={isSaving}>
                        <InputLabel id="name-color-label">Name Color</InputLabel>
                        <Select
                            labelId="name-color-label"
                            label="Name Color"
                            name="name_color"
                            value={formData.name_color}
                            onChange={handleChange}
                        >
                            {colorOptions.map((color) => (
                                <MenuItem key={color.value} value={color.value}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Box sx={{ width: 20, height: 20, backgroundColor: `#${color.value}`, border: '1px solid grey', borderRadius: '4px' }} />
                                        <Typography variant="body2">{color.label} (#{color.value})</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="textSecondary" sx={{ pl: '14px', pt: '3px' }}>
                            Hex color code for the name.
                        </Typography>
                    </FormControl>
                    {/* --- END MODIFICATION --- */}
                    <TextField
                        label="Icon URL"
                        name="icon_url"
                        value={formData.icon_url}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Steam Economy Image URL or path"
                        helperText="URL or path for the item's image."
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSaving}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Item')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


// --- Main Featured Item Management Page Component ---
function FeaturedItemManagementPage() {
    const [featuredItems, setFeaturedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null); // For editing

    // Fetch items function
    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getFeaturedItems();
            // Sort items, e.g., by name or assetid
            const sortedData = [...(data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setFeaturedItems(sortedData);
        } catch (err) {
            setError('Failed to load featured items. Please try refreshing.');
            console.error("Fetch featured items error:", err.response?.data || err.message || err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        document.title = 'SpinnerCS - Admin: Featured';
        fetchItems();
    }, [fetchItems]);

    // --- Action Handlers ---
    const handleOpenAddModal = () => {
        setSelectedItem(null); // Ensure no item is selected for adding
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (itemToEdit) => {
        setSelectedItem(itemToEdit);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null); // Clear selection on close
    };

    const handleSaveItem = async (itemData, isEditMode) => {
        // API call is made within the modal, refresh list after save
        if (isEditMode) {
            await updateFeaturedItem(itemData.assetid, itemData);
        } else {
            await addFeaturedItem(itemData);
        }
        fetchItems(); // Refresh the list
    };

    const handleDeleteItem = async (assetId, itemName) => {
        const confirmDelete = window.confirm(`Are you sure you want to remove "${itemName || assetId}" from featured items?`);
        if (confirmDelete) {
            setLoading(true); // Use main loading indicator for delete
            setError(null);
            try {
                await deleteFeaturedItem(assetId);
                // Refresh list after successful deletion
                setFeaturedItems(prevItems => prevItems.filter(i => i.assetid !== assetId));
            } catch (err) {
                setError(`Failed to delete featured item ${assetId}.`);
                console.error(`Delete featured item ${assetId} error:`, err.response?.data || err.message || err);
            } finally {
                setLoading(false);
            }
        }
    };

    // --- Render Logic ---
    if (loading && featuredItems.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Featured Item Management</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh List">
                        <IconButton onClick={fetchItems} disabled={loading} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddModal}
                        disabled={loading}
                    >
                        Add Featured Item
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="featured item table" size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            <TableCell>Icon</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Asset ID</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {featuredItems.map((item) => (
                            <TableRow
                                key={item.assetid}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell sx={{ p: 0.5 }}>
                                    <Avatar
                                        variant="square"
                                        src={getSteamImageUrl(item.icon_url)}
                                        alt={item.name?.substring(0, 1) || '?'}
                                        sx={{ width: 40, height: 40, bgcolor: 'grey.700' }}
                                    />
                                </TableCell>
                                <TableCell sx={{ color: item.name_color ? `#${item.name_color}` : 'inherit', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Tooltip title={item.name || 'N/A'} placement="top-start">
                                        <span>{item.name || 'N/A'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{item.assetid}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Edit Featured Item">
                                        <IconButton onClick={() => handleOpenEditModal(item)} color="primary" size="small" sx={{ mr: 0.5 }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove from Featured">
                                        <IconButton
                                            onClick={() => handleDeleteItem(item.assetid, item.name)}
                                            color="error"
                                            size="small"
                                            disabled={loading}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {featuredItems.length === 0 && !loading && (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                    No featured items found. Add some using the button above.
                </Typography>
            )}

            {/* Add/Edit Modal */}
            <FeaturedItemModal
                open={isModalOpen}
                onClose={handleCloseModal}
                item={selectedItem} // Pass null for adding, item object for editing
                onSave={handleSaveItem}
            />
        </Box>
    );
}

export default FeaturedItemManagementPage;
